import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Target, Palette, Share2, Headphones, Music, BarChart2,
  CheckCircle, ArrowRight, Send, Clock, FileText, MessageCircle
} from 'lucide-react';
import './ForArtistsPage.css';

const ForArtistsPage = () => {
  const pageRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    if (pageRef.current) {
      const fadeElements = pageRef.current.querySelectorAll('.fade-in');
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      }, { threshold: 0.1 });

      fadeElements.forEach(el => observer.observe(el));
      return () => observer.disconnect();
    }
  }, []);

  const focusPoints = [
    "Artist positioning",
    "Release strategy",
    "Social rollout",
    "Visual identity",
    "Streaming support",
    "Beatport and DJ-facing visibility",
    "Catalog thinking",
    "Long-term momentum"
  ];

  const features = [
    {
      icon: Target,
      title: "Release Strategy",
      description: "We map the release beyond the upload date — timing, positioning, storytelling, and how each drop fits into the bigger picture."
    },
    {
      icon: Palette,
      title: "Visual Direction",
      description: "We build the visual language around the music, from release assets and artist intros to campaign graphics and supporting content."
    },
    {
      icon: Share2,
      title: "Social Rollout",
      description: "We create structured promotional campaigns for short-form content, launch moments, artist storytelling, and ongoing audience growth."
    },
    {
      icon: Headphones,
      title: "Streaming + Platform Support",
      description: "We think beyond a single DSP. We build for the full ecosystem — from Spotify and Apple Music to YouTube, short-form discovery, and DJ-facing channels."
    },
    {
      icon: Music,
      title: "Beatport + DJ Positioning",
      description: "For the right records, we support club-facing visibility with Beatport-aware planning, DJ utility, and music that can live in playlists, charts, and sets."
    }
  ];

  const processSteps = [
    {
      number: "01",
      title: "Fit",
      description: "We look for alignment in sound, intent, and direction."
    },
    {
      number: "02",
      title: "Planning",
      description: "Once there's a fit, we define the release story, timeline, and content requirements."
    },
    {
      number: "03",
      title: "Delivery",
      description: "We gather the essential materials — audio, artwork, metadata, press images, video clips, and platform info."
    },
    {
      number: "04",
      title: "Rollout",
      description: "We build the campaign across social, streaming, and — where relevant — Beatport and DJ-facing channels."
    },
    {
      number: "05",
      title: "Momentum",
      description: "Release day is not the finish line. We keep building after launch through follow-up content, support proof, and catalog growth."
    }
  ];

  return (
    <div className="for-artists-page" ref={pageRef} data-testid="for-artists-page">
      {/* Hero Section */}
      <section className="fa-hero">
        <div className="fa-hero-bg">
          <img 
            src="https://images.unsplash.com/photo-1763630052658-495b36d59937?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxuaWdodGNsdWIlMjBESiUyMHBlcmZvcm1hbmNlJTIwdHVybnRhYmxlc3xlbnwwfHx8fDE3NzQ5OTExNDl8MA&ixlib=rb-4.1.0&q=85" 
            alt="DJ performing"
            className="fa-hero-image"
          />
          <div className="fa-hero-overlay"></div>
        </div>
        <div className="fa-hero-content fade-in">
          <span className="fa-overline">For Artists</span>
          <h1 className="fa-hero-title">
            We build releases with identity, strategy, and room to grow
          </h1>
          <p className="fa-hero-text">
            Anyone Music Records works with electronic artists creating music that feels distinct, 
            intentional, and built to last. We support releases with rollout strategy, visual direction, 
            artist positioning, and club-aware thinking across both streaming platforms and DJ culture.
          </p>
          <div className="fa-hero-ctas">
            <Link to="/submit-demo" className="btn btn-primary">
              <Send size={18} /> Submit Music
            </Link>
            <Link to="/contact" className="btn btn-secondary">
              <MessageCircle size={18} /> Get in Touch
            </Link>
          </div>
        </div>
      </section>

      {/* Why AMR Section */}
      <section className="fa-section fa-why">
        <div className="fa-section-inner">
          <div className="fa-section-header fade-in">
            <span className="fa-section-label">Why AMR</span>
            <h2 className="fa-section-title">
              More than distribution. More than a release day post.
            </h2>
            <p className="fa-section-intro">
              We believe strong records deserve more than a basic upload and a few promotional graphics. 
              At AMR, we approach each release as part of a larger artist story — one that connects sound, 
              visuals, strategy, and scene positioning. We work to create momentum before release, impact 
              on release day, and continued value after release through content, catalog thinking, and 
              support across the platforms that matter.
            </p>
          </div>
          
          <div className="fa-why-content fade-in">
            <div className="fa-why-image">
              <img 
                src="https://images.unsplash.com/photo-1712530930151-9483e1b489b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxlbGVjdHJvbmljJTIwbXVzaWMlMjBESiUyMHN0dWRpbyUyMHByb2R1Y3Rpb258ZW58MHx8fHwxNzc0OTkxMTQ3fDA&ixlib=rb-4.1.0&q=85" 
                alt="Music production studio"
              />
            </div>
            <div className="fa-why-points">
              <h3>What we focus on</h3>
              <ul className="fa-focus-list">
                {focusPoints.map((point, index) => (
                  <li key={index}>
                    <CheckCircle size={18} className="check-icon" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="fa-section fa-features">
        <div className="fa-section-inner">
          <div className="fa-section-header fade-in">
            <span className="fa-section-label">What We Do</span>
            <h2 className="fa-section-title">
              Support designed for modern electronic releases
            </h2>
            <p className="fa-section-intro">
              Every artist and every record is different, but our support is built around a core set 
              of priorities that help releases travel further and land stronger.
            </p>
          </div>
          
          <div className="fa-features-grid fade-in">
            {features.map((feature, index) => (
              <div key={index} className="fa-feature-card">
                <div className="fa-feature-icon">
                  <feature.icon size={28} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Work Section */}
      <section className="fa-section fa-process">
        <div className="fa-section-inner">
          <div className="fa-section-header fade-in">
            <span className="fa-section-label">How We Work</span>
            <h2 className="fa-section-title">
              A cleaner process makes for a stronger campaign
            </h2>
            <p className="fa-section-intro">
              We like to work with artists who are serious about the music and responsive with the details. 
              The stronger the foundation, the stronger the rollout.
            </p>
          </div>
          
          <div className="fa-process-steps fade-in">
            {processSteps.map((step, index) => (
              <div key={index} className="fa-process-step">
                <div className="fa-step-number">{step.number}</div>
                <div className="fa-step-content">
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Look For Section */}
      <section className="fa-section fa-looking-for">
        <div className="fa-section-inner">
          <div className="fa-looking-content fade-in">
            <div className="fa-looking-text">
              <span className="fa-section-label">What We're Looking For</span>
              <h2 className="fa-section-title">
                Music with clarity, character, and replay value
              </h2>
              <p className="fa-section-intro">
                We're interested in electronic music that feels intentional. Records with shape. 
                Records with mood. Records that know what they are.
              </p>
              <p className="fa-supporting-text">
                That doesn't mean one genre, one BPM, or one formula. It means music with identity — 
                something an artist can stand behind and something a listener or DJ can return to.
              </p>
            </div>
            <div className="fa-looking-image">
              <img 
                src="https://images.pexels.com/photos/27007090/pexels-photo-27007090.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" 
                alt="Music producer at work"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What to Send Section */}
      <section className="fa-section fa-submission">
        <div className="fa-section-inner">
          <div className="fa-section-header fade-in">
            <span className="fa-section-label">What to Send</span>
            <h2 className="fa-section-title">
              Help us understand the record — not just hear it
            </h2>
            <p className="fa-section-intro">
              When you submit music to AMR, give us enough context to understand both the track 
              and the artist behind it.
            </p>
          </div>
          
          <div className="fa-submission-details fade-in">
            <div className="fa-submission-card">
              <FileText size={32} className="submission-icon" />
              <h3>Submission Requirements</h3>
              <p>
                Please include a private streaming link, a short artist bio, links to your current platforms, 
                and a few lines on why the release fits the world of Anyone Music Records. If the track is 
                intended for club play, include any useful details around BPM, key, genre, and where you see 
                it working in a set.
              </p>
              <p className="fa-note">
                <Clock size={16} /> Please avoid sending large attachments unless requested.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="fa-section fa-cta-section">
        <div className="fa-cta-bg">
          <img 
            src="https://images.unsplash.com/photo-1764510383709-14be6ec28548?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxuaWdodGNsdWIlMjBESiUyMHBlcmZvcm1hbmNlJTIwdHVybnRhYmxlc3xlbnwwfHx8fDE3NzQ5OTExNDl8MA&ixlib=rb-4.1.0&q=85" 
            alt="DJ performance"
            className="fa-cta-image"
          />
          <div className="fa-cta-overlay"></div>
        </div>
        <div className="fa-cta-content fade-in">
          <h2>Think your music belongs here?</h2>
          <p>
            If it aligns with the sound, the identity, and the direction of AMR, send it through.
          </p>
          <div className="fa-cta-buttons">
            <Link to="/submit-demo" className="btn btn-primary btn-large">
              <Send size={20} /> Submit a Demo
            </Link>
            <Link to="/contact" className="btn btn-secondary btn-large">
              Contact the Label <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForArtistsPage;
